import React, { useEffect, useState } from 'react';
import { getFuturesKeysStatus, saveFuturesKeys, getFuturesAccount } from '../../services/FuturesService';
import { getUserExchanges } from '../../services/UserExchangesService';
import { processError } from '../../services/ServiceUtils';

/**
 * Gerencia as chaves de API de FUTUROS, atreladas à corretora PRIMARY (as chaves vivem por
 * corretora em userExchanges). Só corretoras com providesFuturesApi=true (hoje Binance) têm
 * futuros — pras outras, exibe aviso. O testnet de futuros tem credenciais PRÓPRIAS, distintas
 * do spot. props: exchanges (metadata do getProviders), onChange(status) opcional.
 */
function FuturesKeysManager({ exchanges, onChange }) {
    const [status, setStatus] = useState({ testConfigured: false, productionConfigured: false });
    const [form, setForm] = useState({ testFuturesKey: '', testFuturesSecret: '', futuresKey: '', futuresSecret: '' });
    const [message, setMessage] = useState(null);
    const [busy, setBusy] = useState(false);
    const [primary, setPrimary] = useState(null);
    const [testing, setTesting] = useState(null);       // 'test' | 'production' em teste agora
    const [testResult, setTestResult] = useState({});   // { test: {...}, production: {...} }

    useEffect(() => {
        getFuturesKeysStatus()
            .then(setStatus)
            .catch(err => setMessage({ ok: false, text: processError(err) }));
        getUserExchanges()
            .then(rows => setPrimary((rows || []).find(r => r.isPrimary && r.isActive) || null))
            .catch(() => {});
    }, []);

    const primaryMeta = primary && (exchanges || []).find(e => e.name === primary.exchangeName);
    const primaryLabel = primaryMeta ? primaryMeta.label : (primary && primary.exchangeName);
    // Só mostra os campos quando a corretora primary tem API de futuros. Default permissivo
    // (true) enquanto a lista ainda não carregou, pra não esconder os campos por um instante.
    const supportsFutures = !primary || !primaryMeta || primaryMeta.providesFuturesApi !== false;

    function save(environment) {
        const payload = environment === 'test'
            ? { testFuturesKey: form.testFuturesKey, testFuturesSecret: form.testFuturesSecret }
            : { futuresKey: form.futuresKey, futuresSecret: form.futuresSecret };
        setBusy(true);
        saveFuturesKeys(payload)
            .then(st => {
                setStatus(st);
                if (onChange) onChange(st);
                setForm({ testFuturesKey: '', testFuturesSecret: '', futuresKey: '', futuresSecret: '' });
                setMessage({ ok: true, text: `Chaves de futuros (${environment === 'test' ? 'testnet' : 'produção'}) salvas.` });
            })
            .catch(err => setMessage({ ok: false, text: processError(err) }))
            .finally(() => setBusy(false));
    }

    // Testa o acesso lendo a conta de margem com as chaves SALVAS do ambiente (mesmo endpoint da
    // tela de Futuros). Sucesso = conexão+permissão OK; erro = chave inválida/sem permissão Futures.
    function testAccess(environment) {
        setTesting(environment);
        setTestResult(prev => ({ ...prev, [environment]: null }));
        getFuturesAccount(environment)
            .then(acc => {
                const bal = acc && acc.balance ? `${parseFloat(acc.balance.balance).toFixed(2)} USDT` : 'sem saldo';
                const pos = acc && acc.positions ? acc.positions.length : 0;
                setTestResult(prev => ({ ...prev, [environment]: { ok: true, text: `Acesso OK — margem ${bal} · ${pos} posição(ões).` } }));
            })
            .catch(err => setTestResult(prev => ({ ...prev, [environment]: { ok: false, text: processError(err) } })))
            .finally(() => setTesting(null));
    }

    function block(environment) {
        const isTest = environment === 'test';
        const configured = isTest ? status.testConfigured : status.productionConfigured;
        const keyField = isTest ? 'testFuturesKey' : 'futuresKey';
        const secretField = isTest ? 'testFuturesSecret' : 'futuresSecret';
        return (
            <div className={`border rounded p-3 mb-3 ${isTest ? 'border-warning' : ''}`}>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                    <strong>{isTest ? 'Testnet (Demo Trading)' : 'Produção'}</strong>
                    {configured
                        ? <span className='badge bg-success'>configurada</span>
                        : <span className='badge bg-secondary'>não configurada</span>}
                </div>
                <div className='row'>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label small'>API Key</label>
                        <input type='text' className='form-control form-control-sm'
                            value={form[keyField]}
                            onChange={e => setForm({ ...form, [keyField]: e.target.value })}
                            placeholder={configured ? '•••••• (em branco = manter)' : 'cole a API Key'} />
                    </div>
                    <div className='col-md-6 mb-2'>
                        <label className='form-label small'>Secret Key</label>
                        <input type='password' className='form-control form-control-sm'
                            value={form[secretField]}
                            onChange={e => setForm({ ...form, [secretField]: e.target.value })}
                            placeholder={configured ? '•••••• (em branco = manter)' : 'cole a Secret Key'} />
                    </div>
                </div>
                <div className='d-flex gap-2 align-items-center flex-wrap'>
                    <button className='btn btn-primary btn-sm' disabled={busy} onClick={() => save(environment)}>
                        Salvar {isTest ? 'testnet' : 'produção'}
                    </button>
                    <button className='btn btn-outline-info btn-sm' disabled={!configured || testing === environment}
                        title={configured ? 'Lê a conta de margem com as chaves salvas' : 'Salve as chaves primeiro'}
                        onClick={() => testAccess(environment)}>
                        {testing === environment ? 'Testando…' : 'Testar acesso'}
                    </button>
                </div>
                {testResult[environment] && (
                    <div className={`small mt-2 ${testResult[environment].ok ? 'text-success' : 'text-danger'}`}>
                        {testResult[environment].ok ? '✅ ' : '⚠ '}{testResult[environment].text}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div>
            <div className='d-flex justify-content-between align-items-center mb-2'>
                <h5 className='mb-0'>📈 Chaves de API de Futuros (Derivativos USDT-M)</h5>
                {primaryLabel && (
                    <span className='small text-muted'>
                        Corretora: <span className='badge bg-success'>{primaryLabel}</span>
                    </span>
                )}
            </div>

            {!supportsFutures ? (
                <div className='alert alert-warning py-2' style={{ fontSize: 13 }}>
                    A corretora primary <strong>{primaryLabel}</strong> ainda não oferece API de futuros no Hydra.
                    Defina uma corretora com suporte a futuros (ex.: <strong>Binance</strong>) como primary em modo Spot
                    para configurar as chaves de futuros dela.
                </div>
            ) : (
                <>
                    <p className='text-muted small'>
                        As chaves de futuros ficam atreladas à corretora <strong>{primaryLabel || 'primary'}</strong>.
                        O testnet de futuros (Demo Trading) tem chaves <strong>próprias</strong>, separadas do spot — gere em
                        demo.binance.com → API Management (tipo <em>System generated</em>, com permissão Futures).
                        Para produção, use uma chave da Binance com Futures habilitado.
                    </p>
                    {message && (
                        <div className={`alert ${message.ok ? 'alert-success' : 'alert-danger'} py-2`}>{message.text}</div>
                    )}
                    {block('test')}
                    {block('production')}
                </>
            )}
        </div>
    );
}

export default FuturesKeysManager;
